// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { AssetMetadataUpdate, updateAsset } from '@/asset/api/update-asset';
import { AssetErrorBoundary } from '@/asset/components/asset-error-boundary';
import { MetadataForm } from '@/asset/components/metadata/metadata-form';
import { useAsset } from '@/asset/hooks/use-asset';
import {
  AssetMetadataForm,
  assetMetadataFormSchema,
} from '@/asset/utilities/metadata';
import { HardfinTabCardWrapper } from '@/components/hardfin-tab-card-wrapper';
import { Link } from '@/components/link';
import { UnsavedChangesDialog } from '@/components/unsaved-changes-dialog';
import { SettingsRounded } from '@/constants/hardfin-standard-icons';
import { useDisclosure } from '@/hooks/use-disclosure';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  Collapse,
  IconButton,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import { AdapterLuxon } from '@mui/x-date-pickers-pro/AdapterLuxon';
import { LocalizationProvider } from '@mui/x-date-pickers-pro/LocalizationProvider';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useBlocker } from '@tanstack/react-router';
import { useRef } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';

export const Route = createFileRoute('/_app/assets/$assetKey/metadata')({
  component: AssetMetadataRoute,
});

function AssetMetadataRoute() {
  const { assetKey } = Route.useParams();

  return (
    <AssetErrorBoundary>
      <AssetMetadataContent assetKey={assetKey} />
    </AssetErrorBoundary>
  );
}

function AssetMetadataContent(props: { assetKey: string }) {
  const { asset } = useAsset(props.assetKey);
  const dialog = useDisclosure();
  const queryClient = useQueryClient();
  const configurationButtonRef = useRef<HTMLButtonElement>(null);

  const methods = useForm<AssetMetadataForm>({
    resolver: zodResolver(assetMetadataFormSchema),
    defaultValues: {
      metadata: asset.metadata,
    },
  });

  const { reset, formState, handleSubmit } = methods;

  const mutation = useMutation({
    mutationFn: (updates: AssetMetadataUpdate[]) => {
      return updateAsset(props.assetKey, { metadata: updates });
    },
    onSuccess: async ({ asset }) => {
      reset({
        metadata: asset.metadata,
      });

      await queryClient.invalidateQueries({
        queryKey: ['asset', props.assetKey],
      });
    },
  });

  const blocker = useBlocker({ condition: formState.isDirty });
  const isBlocked = blocker.status === 'blocked';

  const handleClickReset = () => {
    // Reset the entire form with the original values
    reset({
      metadata: asset.metadata,
    });
  };

  const handleValidSubmission: SubmitHandler<AssetMetadataForm> = (data) => {
    if (formState.dirtyFields.metadata === undefined) {
      return;
    }
    const updates = formState.dirtyFields.metadata.reduce<
      AssetMetadataUpdate[]
    >((updates, isUpdated, index) => {
      if (isUpdated) {
        const metadata = asset.metadata[index];
        return [
          ...updates,
          {
            fieldId: metadata.field.id,
            value: data.metadata[index].value,
          },
        ];
      }
      return updates;
    }, []);

    mutation.mutate(updates);
  };

  return (
    <FormProvider {...methods}>
      <LocalizationProvider dateAdapter={AdapterLuxon}>
        <HardfinTabCardWrapper
          title="Asset details"
          actions={
            <IconButton
              ref={configurationButtonRef}
              size="small"
              onClick={dialog.open}
            >
              <SettingsRounded fontSize="small" />
            </IconButton>
          }
        >
          <Box component="form" onSubmit={handleSubmit(handleValidSubmission)}>
            <UnsavedChangesDialog
              isOpen={isBlocked}
              onCancel={blocker.reset}
              onProceed={blocker.proceed}
            />
            <Popover
              anchorEl={configurationButtonRef.current}
              open={dialog.isOpen}
              onClose={dialog.close}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <Typography padding={2} width="15rem" maxWidth="100%">
                Fields for this asset are configured in{' '}
                <Link
                  to="/catalog/items/$itemKey"
                  params={{ itemKey: asset.item.key }}
                >
                  {asset.item.name}
                </Link>
              </Typography>
            </Popover>
            <Box
              display="grid"
              rowGap={2}
              columnGap={4}
              gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
              padding={2}
              marginTop={1}
            >
              <MetadataForm metadata={asset.metadata} />
            </Box>
            <Collapse in={formState.isDirty}>
              <Stack spacing={1} direction="row" justifyContent="end">
                <Button onClick={handleClickReset}>Reset</Button>
                <Button type="submit" variant="contained" color="secondary">
                  Save
                </Button>
              </Stack>
            </Collapse>
          </Box>
        </HardfinTabCardWrapper>
      </LocalizationProvider>
    </FormProvider>
  );
}
